import type { Disposable } from "@/shared/contracts/lifecycle";
import {
  resetFloatingTooltip,
  startFloatingTooltip,
  type FloatingTooltipCleanup,
} from "@/shared/ui/floatingTooltip";

export interface HintTooltipOptions {
  readonly id: string;
  readonly label: string;
  readonly text: string;
}

export interface HintTooltipView extends Disposable {
  readonly root: HTMLSpanElement;
}

const HINT_FLOATING_TOOLTIP_OPTIONS = {
  preferredPlacement: "top",
} as const;

export function createHintTooltip(
  options: HintTooltipOptions,
): HintTooltipView {
  const root = document.createElement("span");
  root.className = "hover-idle-hint-tooltip";
  root.dataset.open = "false";

  const trigger = document.createElement("button");
  trigger.type = "button";
  trigger.className = "hover-idle-hint-tooltip-trigger";
  trigger.textContent = "?";
  trigger.setAttribute("aria-label", options.label);
  trigger.setAttribute("aria-describedby", options.id);
  trigger.setAttribute("aria-expanded", "false");

  const tooltip = document.createElement("span");
  tooltip.id = options.id;
  tooltip.className = "hover-idle-hint-tooltip-content";
  tooltip.dataset.placement = "top";
  tooltip.setAttribute("role", "tooltip");
  tooltip.setAttribute("aria-hidden", "true");
  tooltip.hidden = true;
  tooltip.textContent = options.text;

  let floatingCleanup: FloatingTooltipCleanup | null = null;

  const cleanupFloatingPosition = (): void => {
    const cleanup = floatingCleanup;
    floatingCleanup = null;
    if (cleanup === null) return;
    cleanup();
    resetFloatingTooltip(tooltip);
  };

  const startFloatingPosition = (): void => {
    cleanupFloatingPosition();
    floatingCleanup = startFloatingTooltip(
      trigger,
      tooltip,
      HINT_FLOATING_TOOLTIP_OPTIONS,
    );
  };

  const setOpen = (open: boolean): void => {
    root.dataset.open = open ? "true" : "false";
    tooltip.hidden = !open;
    tooltip.setAttribute("aria-hidden", open ? "false" : "true");
    trigger.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) {
      startFloatingPosition();
    } else {
      cleanupFloatingPosition();
    }
  };

  const show = (): void => setOpen(true);
  const hide = (): void => setOpen(false);
  const handleClick = (): void => show();
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    hide();
    event.stopPropagation();
  };

  trigger.addEventListener("pointerenter", show);
  trigger.addEventListener("pointerleave", hide);
  trigger.addEventListener("focus", show);
  trigger.addEventListener("blur", hide);
  trigger.addEventListener("click", handleClick);
  trigger.addEventListener("keydown", handleKeyDown);

  root.append(trigger, tooltip);

  return {
    root,
    dispose(): void {
      trigger.removeEventListener("pointerenter", show);
      trigger.removeEventListener("pointerleave", hide);
      trigger.removeEventListener("focus", show);
      trigger.removeEventListener("blur", hide);
      trigger.removeEventListener("click", handleClick);
      trigger.removeEventListener("keydown", handleKeyDown);
      cleanupFloatingPosition();
      root.remove();
    },
  };
}
